import React from 'react';
import { common, query } from '@kurtosys/ksys-app-template';
import { IComponentStyle } from '@kurtosys/types/componentStyle/IComponentStyle.js';

import { IComponentBaseConfiguration } from '../../../../models/IComponentBaseConfiguration.js';
import { IComponentBaseState } from '../../../../models/IComponentBaseState.js';
import { ComponentBase } from '../../../../shared/index.js';
import { ITableTooltip } from '../models/index.js';
import { ITableColumn } from '../models/ITableColumn.js';
import { ITableComponentBaseProps } from '../models/ITableComponentBaseProps.js';
import { ITableEvents } from '../models/ITableEvents.js';
import { ITableEventsElement } from '../models/ITableEventsElement.js';
import { ITableGroup } from '../models/ITableGroup.js';
import { ITableHeaderGroup } from '../models/ITableHeaderGroup.js';
import { ITableRow } from '../models/ITableRow.js';
import { Table } from '../Table.js';
import { TableLogicBase } from '../TableLogicBase.js';

export abstract class TableComponentBase<
	TProps extends ITableComponentBaseProps,
	TState extends IComponentBaseState,
	TConfiguration extends IComponentBaseConfiguration,
> extends ComponentBase<TProps, TState, TConfiguration> {
	abstract get tableEventsKey(): keyof ITableEvents | undefined;

	get tableEvents(): ITableEvents {
		return (this.table && this.table.events) || {};
	}

	get eventsForElement(): ITableEventsElement<any> {
		return (this.tableEventsKey && this.tableEvents[this.tableEventsKey]) || {};
	}

	get table(): Table<any> {
		return this.props.table;
	}

	getRowKey(row: ITableRow, rowIndex: number) {
		if (row && row['_rowKey']) {
			return row['_rowKey'];
		}
		return `row_${rowIndex}`;
	}

	getCellKey(row: ITableRow, rowIndex: number, column: ITableColumn) {
		const { id } = column;
		return `${this.getRowKey(row, rowIndex)}_${id || 'NO_ID'}`;
	}

	getHeadRowKey(rowIndex: number) {
		return `head_row_${rowIndex}`;
	}

	getHeadCellKey(rowIndex: number, columnIndex: number, column: ITableColumn | ITableHeaderGroup) {
		const { id } = column;
		return `${this.getHeadRowKey(rowIndex)}_${id || 'NO_ID'}_${columnIndex || -1}`;
	}

	getRowStyleProps(row: ITableRow, rowIndex: number, grouping?: ITableGroup | null) {
		const componentStyleProps = {
			row,
			rowIndex,
		};
		const { rowStyle, rowStyleQueries, rowClasses } = this.table.props;
		let className = this.table.getConditionalClassName(componentStyleProps, rowClasses);
		let componentStyle = this.orchestrateComponentStyle(componentStyleProps, rowStyle, rowStyleQueries);
		if (grouping) {
			const {
				rowStyle: groupRowStyle,
				rowStyleQueries: groupRowStyleQueries,
				rowClasses: groupRowClasses,
			} = grouping;
			if (groupRowClasses && groupRowClasses.length > 0) {
				className = this.table.getConditionalClassName(componentStyleProps, groupRowClasses, className);
			}
			if (groupRowStyle || groupRowStyleQueries) {
				const groupComponentStyle = this.orchestrateComponentStyle(
					componentStyleProps,
					groupRowStyle,
					groupRowStyleQueries,
				);
				componentStyle = common.commonUtils.deepMergeObjects(componentStyle, groupComponentStyle);
			}
		}
		return {
			className,
			componentStyleProps,
			componentStyle,
		};
	}

	getCellStyleProps(row: ITableRow, rowIndex: number, column: ITableColumn, columnIndex: number, cellValue: any) {
		const { cellStyle, cellStyleQueries } = column;
		const componentStyleProps = {
			row,
			rowIndex,
			column,
			columnIndex,
			cellValue,
		};
		const { conditionalStyles } = this.table.props;
		let componentStyle = this.orchestrateComponentStyle(componentStyleProps, cellStyle, cellStyleQueries);

		if (!common.commonUtils.isNullOrEmpty(conditionalStyles)) {
			const conditonalsObj = {
				row,
				rowIndex,
				column,
				columnIndex,
				cellValue,
			};
			for (const conditionalStyle of conditionalStyles) {
				const conditionalHelper = new common.helpers.ConditionalHelper(
					{ queryClass: query.Query },
					conditionalStyle.conditional,
				);
				if (conditionalHelper.matches(conditonalsObj)) {
					componentStyle = {
						...componentStyle,
						...conditionalStyle.style,
					};
				}
			}
		}

		const className = this.table.getConditionalClassName(componentStyleProps, column.cellClasses);
		return {
			className,
			componentStyleProps,
			componentStyle,
		};
	}

	getHeadCellStyleProps(rowIndex: number, column: ITableColumn | ITableHeaderGroup, columnIndex: number) {
		const { header, headCellStyle, headCellStyleQueries } = column;
		const componentStyleProps = {
			column,
			columnIndex,
			header,
		};
		const { conditionalHeadStyles } = this.table.props;

		let componentStyle = this.orchestrateComponentStyle(componentStyleProps, headCellStyle, headCellStyleQueries);

		if (!common.commonUtils.isNullOrEmpty(conditionalHeadStyles)) {
			const conditonalsObj = {
				rowIndex,
				column,
				columnIndex,
				cellValue: header,
			};
			for (const conditionalStyle of conditionalHeadStyles) {
				const conditionalHelper = new common.helpers.ConditionalHelper(
					{ queryClass: query.Query },
					conditionalStyle.conditional,
				);
				if (conditionalHelper.matches(conditonalsObj)) {
					componentStyle = {
						...componentStyle,
						...conditionalStyle.style,
					};
				}
			}
		}
		const className = this.table.getConditionalClassName(componentStyleProps, column.headCellClasses);
		return {
			className,
			componentStyleProps,
			componentStyle,
		};
	}

	orchestrateComponentStyle = (componentStyleProps: any, initialStyle?: IComponentStyle, styleQueries?: any) => {
		let componentStyle: IComponentStyle | undefined = initialStyle;
		const { queryStore } = this.table.props;
		if (queryStore && styleQueries) {
			componentStyle = queryStore.mergeQueriesAndProps(
				{
					props: initialStyle || {},
					queries: styleQueries,
				},
				componentStyleProps,
			);
		}
		return componentStyle;
	};

	getEvents<T extends HTMLElement>(
		eventsForElement: ITableEventsElement<T> = {},
		row?: any,
		rowIndex?: number,
		column?: ITableColumn | ITableHeaderGroup,
		columnIndex?: number,
		cellValue?: any,
	) {
		const response: React.DOMAttributes<T> = {};
		const keys = Object.keys(eventsForElement) as (keyof ITableEventsElement<T>)[];
		for (const key of keys) {
			const handler = eventsForElement[key];
			response[key] = (event) => handler && handler(event, row, rowIndex, column, columnIndex, cellValue);
		}
		return response;
	}

	getTooltipHeaderProps(column: ITableColumn | ITableHeaderGroup, matchingProperties: Partial<ITableTooltip>) {
		const { queryStore } = this.table.props;
		let response;
		if (column && Array.isArray(column.tooltips) && column.tooltips.length > 0) {
			const tooltips = common.commonUtils.filterWithMatchingProperties(column.tooltips, matchingProperties);
			if (tooltips) {
				for (let i = 0; i < tooltips.length; i++) {
					const tooltip = tooltips[i];
					if (tooltip && tooltip.conditional && tooltip.conditional && queryStore) {
						const conditionalTooltipHelper = new common.helpers.ConditionalHelper(
							{ queryClass: query.Query },
							tooltip.conditional,
						);
						if (
							conditionalTooltipHelper.matchesWithOptions({
								executionOptions: queryStore.executionOptions,
							})
						) {
							response = tooltip;
							break;
						}
					} else if (tooltip) {
						response = tooltip;
						break;
					}
				}
			}
		}
		if (response) {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { target, conditional, ...tooltipProps } = response;
			response = tooltipProps;
		}
		return response;
	}

	getTooltipRowProps = TableLogicBase.getTooltipRowProps;
}
